import { fetchOpenAIText } from "../utils/openaiClient";

export function useEssentialVocabularyGeneratorLogic({
  essentialVocabularyInput, setEssentialVocabularyInput, essentialVocabularyMode, setEssentialVocabularyMode,
  essentialVocabularyLevel, essentialVocabularyPartOfSpeech, setEssentialVocabularyPartOfSpeech,
  essentialVocabularyTranslationLanguage, setEssentialVocabularyTranslationLanguage,
  setEssentialVocabularyLoading, generatedEssentialVocabulary, setGeneratedEssentialVocabulary,
  currentTab, onInsertToBoard, activeEditor,
  setShowEssentialVocabularyGenerator, setSelectedTemplate,
}) {
  const handleEssentialVocabularyGenerate = async () => {
    if (!essentialVocabularyInput.trim()) return;

    // If mode is "myWords", just parse the input and create cards
    if (essentialVocabularyMode === 'myWords') {
      setEssentialVocabularyLoading(true);
      try {
        // Split by newlines and commas
        const lines = essentialVocabularyInput.split(/\r?\n/);
        const items = [];

        lines.forEach(line => {
          const trimmed = line.trim();
          if (!trimmed) return;

          // Split by comma, but keep each part as a complete phrase
          const commaParts = trimmed.split(',').map(part => part.trim()).filter(part => part.length > 0);
          items.push(...commaParts);
        });

        // Create vocabulary items from parsed text
        let vocabulary = items.map((phrase, phraseIndex) => {
          const trimmedPhrase = phrase.trim();
          const hasSpaces = trimmedPhrase.includes(' ');
          const hasHyphen = trimmedPhrase.includes('-');
          let detectedType = 'word';

          // Auto-detect type for multi-word items
          if (hasSpaces || hasHyphen) {
            const phraseLower = trimmedPhrase.toLowerCase();
            const idiomPatterns = [
              'break the', 'piece of', 'out of', 'in the', 'on the', 'at the',
              'by the', 'for the', 'with the', 'under the', 'over the',
              'once in a', 'once upon a', 'all of a', 'out of the'
            ];
            const isIdiom = idiomPatterns.some(pattern => phraseLower.includes(pattern));

            const wordCount = trimmedPhrase.split(/\s+/).filter(w => w.length > 0).length;
            if (isIdiom) {
              detectedType = 'idiom';
            } else if (wordCount === 2) {
              detectedType = 'collocation';
            } else {
              detectedType = 'phrase';
            }
          }

          return {
            word: trimmedPhrase,
            definition: '',
            type: detectedType,
            partOfSpeech: ''
          };
        });

        // Generate definitions for all items
        const levelSpecs = {
          A1: { maxWords: 10, temp: 0.1, style: 'Use A1 words only. One simple clause. No idioms. Avoid abstract terms.', example: 'big: large in size; quiet: making little sound' },
          A2: { maxWords: 14, temp: 0.1, style: 'Use A2 everyday words. One simple sentence. No relative clauses or advanced words.', example: 'borrow: take and return later; crowded: full of people' },
          B1: { maxWords: 18, temp: 0.15, style: 'Use B1 words. Clear neutral style. Avoid academic/rare terms.', example: 'flexible: can change or bend easily; efficient: doing work well without waste' },
          B2: { maxWords: 20, temp: 0.2, style: 'Use B2 words. You may use one relative clause. Keep concise.', example: 'sustainable: can continue without causing harm; justify: give good reasons for' },
          C1: { maxWords: 22, temp: 0.2, style: 'Advanced but concise academic tone (C1). Prefer precise, high-utility words.', example: 'ambiguous: having more than one possible meaning; facilitate: make a process easier' },
          C2: { maxWords: 22, temp: 0.2, style: 'Very advanced, precise and concise (C2). One sentence only.', example: 'ubiquitous: present everywhere; empirical: based on observed evidence' },
        };
        const spec = levelSpecs[essentialVocabularyLevel] || { maxWords: 18, temp: 0.15, style: 'Use intermediate words. Clear structure.', example: '' };

        // Generate definitions for all items (with delay to avoid rate limits)
        const generateDefinition = async (item, index) => {
          const word = item.word;
          const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const targetRe = new RegExp(`\\b${escapeRegExp(word)}\\b`, 'i');

          const buildPrompt = (strict = false) => `You are a CEFR-aware lexicographer. Write ONE short English definition that matches the target level.\n\nTarget word/phrase: "${word}"\nLevel: ${essentialVocabularyLevel}\nGuidelines: ${spec.style}\nStyle examples for this level (do not copy, only mimic complexity): ${spec.example}\nHard constraints:\n- Do NOT use the target word (or any inflected form) in the definition\n- Exactly one sentence\n- Max ${spec.maxWords} words\n- Plain text only (no quotes, no markdown, no examples)\n- No meta phrases like "a word that means" or "term for"\n${strict ? '- STRICT retry: If uncertain, prefer a generic hypernym that fits the level.' : ''}`;

          const countWords = (s) => (String(s || '').trim().split(/\s+/).filter(Boolean).length);
          const tooComplexFor = (s, lvl) => {
            const txt = String(s || '');
            if (lvl === 'A1' || lvl === 'A2') {
              if (/[,:;]/.test(txt)) return true;
              if (/(which|that|because|although|despite|furthermore|however|therefore)\b/i.test(txt)) return true;
            }
            return false;
          };

          try {
            // Add delay to avoid rate limits
            await new Promise(resolve => setTimeout(resolve, index * 200));

            let text = await fetchOpenAIText({ prompt: buildPrompt(false), temperature: spec.temp, maxTokens: 120 });
            let firstLine = String(text || '').split('\n')[0].trim();
            if (targetRe.test(firstLine)) {
              text = await fetchOpenAIText({ prompt: buildPrompt(true), temperature: Math.max(0.05, spec.temp - 0.05), maxTokens: 120 });
              firstLine = String(text || '').split('\n')[0].trim();
              if (targetRe.test(firstLine)) {
                firstLine = firstLine.replace(targetRe, 'the term');
              }
            }
            if (countWords(firstLine) > spec.maxWords || tooComplexFor(firstLine, essentialVocabularyLevel)) {
              const refinePrompt = `Rewrite this definition to strictly match CEFR ${essentialVocabularyLevel} constraints. Keep meaning, but simplify or shorten. One sentence, max ${spec.maxWords} words, ${spec.style}. Do not use the target word.\nDefinition: ${firstLine}`;
              const refined = await fetchOpenAIText({ prompt: refinePrompt, temperature: Math.max(0.05, spec.temp - 0.05), maxTokens: 120 });
              const refinedLine = String(refined || '').split('\n')[0].trim();
              if (refinedLine) firstLine = refinedLine;
            }

            return firstLine;
          } catch (error) {
            return '';
          }
        };

        // Generate definitions for all items
        let vocabularyWithDefinitions = await Promise.all(
          vocabulary.map(async (item, index) => {
            const definition = await generateDefinition(item, index);
            return { ...item, definition };
          })
        );

        // Generate translations if language is selected
        if (essentialVocabularyTranslationLanguage && essentialVocabularyTranslationLanguage !== 'none') {
          const generateTranslation = async (item, index) => {
            try {
              await new Promise(resolve => setTimeout(resolve, index * 200));
              const prompt = `Translate the word/phrase "${item.word}" to ${essentialVocabularyTranslationLanguage}. Return only the translation, no explanations or additional text.`;
              const translation = await fetchOpenAIText({ prompt, temperature: 0.3, maxTokens: 50 });
              return translation ? translation.trim() : '';
            } catch (error) {
              return '';
            }
          };

          vocabularyWithDefinitions = await Promise.all(
            vocabularyWithDefinitions.map(async (item, index) => {
              const translation = await generateTranslation(item, index);
              return { ...item, translation };
            })
          );
        }

        setGeneratedEssentialVocabulary({
          vocabulary: vocabularyWithDefinitions,
          title: 'Essential Vocabulary',
          level: essentialVocabularyLevel,
          partOfSpeech: essentialVocabularyPartOfSpeech,
          translationLanguage: essentialVocabularyTranslationLanguage
        });
      } catch (e) {
        // Error processing vocabulary
      } finally {
        setEssentialVocabularyLoading(false);
      }
      return;
    }

    // Otherwise, generate with AI
    setEssentialVocabularyLoading(true);
    try {
      const partOfSpeechText = essentialVocabularyPartOfSpeech === 'any' ? 'any part of speech' : essentialVocabularyPartOfSpeech;
      const prompt = `You are an expert ESL lexicographer. Generate essential vocabulary for classroom use.

TOPIC: ${essentialVocabularyInput}
CEFR_LEVEL: ${essentialVocabularyLevel}
PART_OF_SPEECH: ${partOfSpeechText}

REQUIREMENTS:
- Return ONLY strict JSON (no comments, no markdown, no prose) in this exact shape:
{
  "vocabulary": [
    { "word": "string", "definition": "string", "partOfSpeech": "string", "type": "string" }
  ]
}
- Include 10–15 items.
- Choose high-utility, level-appropriate words/phrases for CEFR ${essentialVocabularyLevel}.
- If PART_OF_SPEECH is not "any", all items MUST match it (use standard labels like noun, verb, adjective, adverb, phrase, collocation).
- For "type" field, use: "word" for single words, "phrase" for multi-word phrases, "idiom" for idioms, "expression" for fixed expressions, "collocation" for word combinations.
- Keep definitions short, learner-friendly, and unambiguous; avoid circular definitions.
- Avoid rare/archaic/slang, brand names, or overly specialized jargon unless topic requires it.
- Prefer core meanings relevant to the given TOPIC.
- No duplicate lemmas. Normalize casing (lowercase unless proper noun).
`;

      const contentRaw = await fetchOpenAIText({ prompt, temperature: 0.35, maxTokens: 900 });
      if (contentRaw) {
        try {
          let content = contentRaw.trim();
          if (content.startsWith('```')) {
            const m = content.match(/```[a-zA-Z]*\n([\s\S]*?)```/);
            if (m && m[1]) content = m[1].trim();
          }
          const parsed = JSON.parse(content);
          let vocabulary = Array.isArray(parsed?.vocabulary) ? parsed.vocabulary : parsed; // backward compatibility if array returned

          // Normalize vocabulary items: ensure type field and auto-detect if missing
          vocabulary = vocabulary.map(item => {
            const word = (item.word || '').trim();
            let type = item.type;

            // Auto-detect type if not provided
            if (!type) {
              const wordLower = word.toLowerCase();
              const wordCount = word.split(/\s+/).filter(w => w.length > 0).length;

              // Check if it's a multi-word phrase
              if (wordCount > 1 || word.includes('-')) {
                // Check partOfSpeech for hints first
                const pos = (item.partOfSpeech || '').toLowerCase();
                if (pos.includes('collocation')) {
                  type = 'collocation';
                } else if (pos.includes('idiom')) {
                  type = 'idiom';
                } else if (pos.includes('expression')) {
                  type = 'expression';
                } else if (pos.includes('phrase')) {
                  type = 'phrase';
                } else {
                  // Try to detect idioms by common patterns
                  const idiomPatterns = [
                    'break the', 'piece of', 'out of', 'in the', 'on the', 'at the',
                    'by the', 'for the', 'with the', 'under the', 'over the',
                    'once in a', 'once upon a', 'all of a', 'out of the'
                  ];
                  const isIdiom = idiomPatterns.some(pattern => wordLower.includes(pattern));

                  if (isIdiom) {
                    type = 'idiom';
                  } else if (wordCount === 2) {
                    // Two-word combinations are often collocations
                    type = 'collocation';
                  } else {
                    // Default to phrase for multi-word items
                    type = 'phrase';
                  }
                }
              } else {
                type = 'word';
              }
            }

            return {
              ...item,
              type: type || 'word',
              word: word,
              definition: (item.definition || '').trim(),
              partOfSpeech: item.partOfSpeech || ''
            };
          });

          // Generate translations if language is selected
          let vocabularyWithTranslations = vocabulary;
          if (essentialVocabularyTranslationLanguage && essentialVocabularyTranslationLanguage !== 'none') {
            const generateTranslation = async (item, index) => {
              try {
                await new Promise(resolve => setTimeout(resolve, index * 200));
                const prompt = `Translate the word/phrase "${item.word}" to ${essentialVocabularyTranslationLanguage}. Return only the translation, no explanations or additional text.`;
                const translation = await fetchOpenAIText({ prompt, temperature: 0.3, maxTokens: 50 });
                return translation ? translation.trim() : '';
              } catch (error) {
                return '';
              }
            };

              vocabularyWithTranslations = await Promise.all(
              vocabulary.map(async (item, index) => {
                const translation = await generateTranslation(item, index);
                return { ...item, translation };
              })
            );
          }

          setGeneratedEssentialVocabulary({
            vocabulary: vocabularyWithTranslations,
            title: 'Essential Vocabulary',
            level: essentialVocabularyLevel,
            partOfSpeech: essentialVocabularyPartOfSpeech,
            translationLanguage: essentialVocabularyTranslationLanguage
          });
        } catch (parseError) {
          setGeneratedEssentialVocabulary({
            vocabulary: [],
            title: 'Essential Vocabulary',
            level: essentialVocabularyLevel,
            partOfSpeech: essentialVocabularyPartOfSpeech
          });
        }
      }
    } catch (e) {
      // Error generating essential vocabulary
    } finally {
      setEssentialVocabularyLoading(false);
    }
  };

  const handleEssentialVocabularyInsert = () => {
    if (currentTab > 0 && onInsertToBoard && generatedEssentialVocabulary) {
      onInsertToBoard(generatedEssentialVocabulary, 'essentialvocabulary');
      setShowEssentialVocabularyGenerator(false);
      setEssentialVocabularyInput("");
      setSelectedTemplate(null);
      return;
    }
    if (generatedEssentialVocabulary && activeEditor) {
      activeEditor.chain().focus().insertEssentialVocabularyExercise({
        vocabulary: generatedEssentialVocabulary.vocabulary,
        title: generatedEssentialVocabulary.title,
        level: generatedEssentialVocabulary.level,
        partOfSpeech: generatedEssentialVocabulary.partOfSpeech
      }).run();
    }

    setShowEssentialVocabularyGenerator(false);
    setEssentialVocabularyInput("");
    setSelectedTemplate(null);
  };

  const handleEssentialVocabularyCancel = () => {
    setShowEssentialVocabularyGenerator(false);
    setEssentialVocabularyInput("");
    setEssentialVocabularyMode('generate');
    setEssentialVocabularyPartOfSpeech('any');
    setEssentialVocabularyTranslationLanguage('none');
    setGeneratedEssentialVocabulary(null);
    setSelectedTemplate(null);
  };

  return { handleEssentialVocabularyGenerate, handleEssentialVocabularyInsert, handleEssentialVocabularyCancel };
}
